import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Table, { Column, DataSourceItem } from "../../../../Components/Atoms/Table";
import Tag from "../../../../Components/Atoms/Tag";
import { useCustomerStore } from "../../../../Store/Customer";
import { PAGINATION_KEYS, usePagination } from "../../../../Store/Pagination";
import { useAuth0 } from "@auth0/auth0-react";
import LoadingPage from "../../../LoadingPage";
import TablePaginationFooter from "../../../../Components/Atoms/Table/TablePaginationFooter";
import { createSortFunction } from "../../../../Utils/TableUtils";
import { toLocalDateString } from "../../../../Utils/DateUtils";
import { PaymentsInvoiceEmailMessageView } from "../../../../Contracts/Customer";

const CustomerDetailEmailHistoryList: React.FC = () => {
  const [sortParamValue, setSortParamValue] = React.useState<string>("updated_at:desc");

  const { limit, page, setLimit, setPage } = usePagination(
    PAGINATION_KEYS.CUSTOMER_DETAIL_EMAIL_HISTORY,
    { limit: 25, page: 1 }
  );

  const { id: customerID } = useParams();

  const columns: Column<PaymentsInvoiceEmailMessageView>[] = [
    {
      title: "Subject",
      dataIndex: "subject",
      key: "subject",
      width: "w-20"
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: d => (
        <Tag
          color={
            d.status === 0 || d.status === "sent" ? "lime" : d.status === "open" ? "blue" : "gray"
          }
        >
          {d.status === 0 ? "Sent" : d.status}
        </Tag>
      ),
      width: "w-24",
      sortClicked: createSortFunction("status", setSortParamValue)
    },
    {
      title: "Recipient",
      dataIndex: "recipient",
      key: "recipient",
      render: d => <div>{d.recipient}</div>,
      width: "w-24"
    },
    {
      title: "Date Issued",
      dataIndex: "date_issued",
      key: "date_issued",
      render: d => (
        <span title={toLocalDateString(d.data.created_at, true)}>
          {toLocalDateString(d.data.created_at)}
        </span>
      ),
      width: "w-24",
      sortClicked: createSortFunction("created_at", setSortParamValue)
    }
  ];

  const { emailHistory, emailHistoryCount, getEmailHistory } = useCustomerStore();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { getAccessTokenSilently } = useAuth0();
  const refreshThings = useCallback(async () => {
    if (!customerID) {
      throw new Error(`Customer ID not found in URL ${window.location.href}`);
    }
    setIsLoading(true);
    await getEmailHistory(getAccessTokenSilently, customerID, limit, sortParamValue, page);
    setIsLoading(false);
  }, [customerID, getEmailHistory, getAccessTokenSilently, limit, sortParamValue, page]);

  useEffect(() => {
    refreshThings();
  }, [refreshThings, limit, sortParamValue, page]);

  const dataSource: DataSourceItem<PaymentsInvoiceEmailMessageView>[] = emailHistory.map(email => ({
    key: email.id.toString(),
    subject: email.subject,
    status: email.status,
    recipient: email.recipients,
    date_issued: toLocalDateString(email.created_at),
    data: email
  }));

  return (
    <div className="flex flex-col h-[calc(100vh-12rem)]">
      <div className="flex flex-grow flex-col overflow-y-auto">
        {isLoading && emailHistory.length === 0 ? (
          <LoadingPage />
        ) : (
          <Table dataSource={dataSource} columns={columns} isLoading={isLoading} />
        )}
        {!isLoading && emailHistory.length === 0 && (
          <div className="text-center text-gray-400 mt-12">No email history found</div>
        )}
      </div>
      <TablePaginationFooter
        limit={limit}
        setLimit={setLimit}
        setPage={setPage}
        totalRecords={emailHistoryCount}
        page={page}
        isLoading={isLoading}
      />
    </div>
  );
};

export default CustomerDetailEmailHistoryList;
